// 007 — Interconsulta Solicitud (anverso) / Informe (reverso) (Fase 7, Tanda 12, ver PLAN.md). Transcripción campo por
// campo del PDF oficial del Anexo 1 MSP: solicitud idx 28 e informe idx 29 (footer `SNS-MSP/HCU-form.007/2021`).
// Informe de brechas en `docs/hce-fase7/brechas-002-003-005-006-007.md`.
//
// Retirado de la UI por no estar impreso: "Especialista / especialidad destino" como campo único (el papel trae
// "Especialidad" y "Especialidad consultada", se capturan ambos sin deducir uno del otro). Decisiones: "Urgente Sí/No" →
// `ServiceRequest.priority` (urgent/routine); `performer.display` = "Especialidad consultada" (mantiene el sentido de
// "destino" que ya tenía). La solicitud y el informe los llenan personas distintas en momentos distintos: se guarda
// solo la mitad con contenido. PRE/DEF solo va en el `Par[]`/PDF.
//
// FHIR (consumidor: 053, que reutiliza el patrón `ServiceRequest` + `Communication.basedOn`): `ServiceRequest` `active`/
// `order`, categoría SNOMED 11429006, y `Communication` `completed` con `basedOn` y `payload.contentString`.
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import { MARCAS_PRE_DEF, OPCIONES_SI_NO, paresDeCampos, valoresVacios, type CampoSimple, type Valores } from './camposSimplesDatos';
import type { Par } from './index';

const area = (clave: string, etiqueta: string): CampoSimple => ({ clave, etiqueta, tipo: 'area' });

export const NUM_DIAGNOSTICOS_007 = 6;

// ---------- Solicitud ----------

export const CAMPOS_CARACTERISTICA_007: readonly CampoSimple[] = [
  { clave: 'servicio', etiqueta: 'Servicio', tipo: 'select', opciones: ['Emergencia', 'Consulta externa', 'Hospitalización'].map((e) => ({ valor: e, etiqueta: e })) },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
  { clave: 'cama', etiqueta: 'No. cama' },
  { clave: 'sala', etiqueta: 'No. sala' },
  { clave: 'urgente', etiqueta: 'Urgente', tipo: 'select', opciones: OPCIONES_SI_NO },
  { clave: 'especialidadConsultada', etiqueta: 'Especialidad consultada' },
  area('motivo', 'Descripción del motivo'),
];
export const CAMPOS_CUADRO_SOLICITUD_007: readonly CampoSimple[] = [area('cuadro', 'Cuadro clínico actual')];
export const CAMPOS_RESULTADOS_007: readonly CampoSimple[] = [area('resultados', 'Resultados de exámenes y procedimientos diagnósticos relevantes')];
export const CAMPOS_PLAN_REALIZADO_007: readonly CampoSimple[] = [area('plan', 'Plan terapéutico realizado')];

export interface EstadoSolicitud007 {
  readonly caracteristica: Valores;
  readonly cuadro: Valores;
  readonly resultados: Valores;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  readonly plan: Valores;
}

export function estadoInicialSolicitud007(): EstadoSolicitud007 {
  return {
    caracteristica: valoresVacios(CAMPOS_CARACTERISTICA_007),
    cuadro: valoresVacios(CAMPOS_CUADRO_SOLICITUD_007),
    resultados: valoresVacios(CAMPOS_RESULTADOS_007),
    diagnosticos: Array.from({ length: NUM_DIAGNOSTICOS_007 }, () => diagnosticoConMarcaVacio()),
    plan: valoresVacios(CAMPOS_PLAN_REALIZADO_007),
  };
}

const paresDiagnosticos = (dx: readonly DiagnosticoConMarca[]): Par[] =>
  dx.map((d, i) => ({
    pregunta: `Diagnóstico ${i + 1}`,
    respuesta: textoDiagnosticoConMarca(d, MARCAS_PRE_DEF),
  }));

export function paresSolicitud007(e: EstadoSolicitud007): Par[] {
  return [
    ...paresDeCampos(CAMPOS_CARACTERISTICA_007, e.caracteristica),
    ...paresDeCampos(CAMPOS_CUADRO_SOLICITUD_007, e.cuadro),
    ...paresDeCampos(CAMPOS_RESULTADOS_007, e.resultados),
    ...paresDiagnosticos(e.diagnosticos),
    ...paresDeCampos(CAMPOS_PLAN_REALIZADO_007, e.plan),
  ].filter((p) => p.respuesta.trim() !== '');
}

/** La solicitud se guarda solo si se llenó algo. */
export const hayContenidoSolicitud007 = (e: EstadoSolicitud007): boolean => paresSolicitud007(e).length > 0;

/**
 * Instructivo del papel (págs. 32 y 34): "Cuadro clínico actual" (solicitud, bloque C) y "Cuadro clínico de interconsulta"
 * (informe, bloque B) se registran "de manera obligatoria". Solo se exige cuando esa mitad se va a guardar (tiene algún
 * contenido): la otra mitad la llena otra persona en otro momento. Devuelve los rótulos de lo que falta.
 */
export function faltantesObligatorios007(solicitud: EstadoSolicitud007, informe: EstadoInforme007): string[] {
  const faltan: string[] = [];
  if (hayContenidoSolicitud007(solicitud) && (solicitud.cuadro.cuadro ?? '').trim() === '') faltan.push(CAMPOS_CUADRO_SOLICITUD_007[0]!.etiqueta);
  if (paresInforme007(informe).length > 0 && (informe.cuadro.cuadro ?? '').trim() === '') faltan.push(CAMPOS_CUADRO_INFORME_007[0]!.etiqueta);
  return faltan;
}

export function prioridadFhir007(urgente: string | undefined): 'urgent' | 'routine' {
  return urgente === 'si' ? 'urgent' : 'routine';
}

/** `ServiceRequest.reasonCode.text` — el motivo si se escribió, si no el cuadro clínico (`SelectorRecurso` lo muestra). */
export function textoMotivo007(e: EstadoSolicitud007): string {
  return (e.caracteristica.motivo ?? '').trim() || (e.cuadro.cuadro ?? '').trim();
}

// ---------- Informe ----------

export const CAMPOS_CUADRO_INFORME_007: readonly CampoSimple[] = [area('cuadro', 'Cuadro clínico de interconsulta')];
export const CAMPOS_CRITERIO_007: readonly CampoSimple[] = [area('criterio', 'Resumen del criterio clínico')];
export const CAMPOS_PLAN_DIAGNOSTICO_007: readonly CampoSimple[] = [area('planDiagnostico', 'Plan de diagnóstico propuesto')];
export const CAMPOS_PLAN_TERAPEUTICO_007: readonly CampoSimple[] = [area('planTerapeutico', 'Plan terapéutico propuesto')];

export interface EstadoInforme007 {
  readonly cuadro: Valores;
  readonly criterio: Valores;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  readonly planDiagnostico: Valores;
  readonly planTerapeutico: Valores;
}

export function estadoInicialInforme007(): EstadoInforme007 {
  return {
    cuadro: valoresVacios(CAMPOS_CUADRO_INFORME_007),
    criterio: valoresVacios(CAMPOS_CRITERIO_007),
    diagnosticos: Array.from({ length: NUM_DIAGNOSTICOS_007 }, () => diagnosticoConMarcaVacio()),
    planDiagnostico: valoresVacios(CAMPOS_PLAN_DIAGNOSTICO_007),
    planTerapeutico: valoresVacios(CAMPOS_PLAN_TERAPEUTICO_007),
  };
}

export function paresInforme007(e: EstadoInforme007): Par[] {
  return [
    ...paresDeCampos(CAMPOS_CUADRO_INFORME_007, e.cuadro),
    ...paresDeCampos(CAMPOS_CRITERIO_007, e.criterio),
    ...paresDiagnosticos(e.diagnosticos),
    ...paresDeCampos(CAMPOS_PLAN_DIAGNOSTICO_007, e.planDiagnostico),
    ...paresDeCampos(CAMPOS_PLAN_TERAPEUTICO_007, e.planTerapeutico),
  ].filter((p) => p.respuesta.trim() !== '');
}

/** `Communication.payload.contentString`: el informe completo como texto (los bloques con contenido, con su rótulo). */
export function textoInforme007(e: EstadoInforme007): string {
  return paresInforme007(e)
    .map((p) => `${p.pregunta}: ${p.respuesta}`)
    .join('\n');
}
